import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { ListItemText, Tooltip } from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDateTime } from 'src/utils/format-time';
import { hasPermission } from 'src/utils/has-permissions';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';

import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

import { IAdvancedPaymentListItem } from 'src/types/advanced-payment';

type Props = {
  selected: boolean;
  onEditRow: VoidFunction;
  row: IAdvancedPaymentListItem;
  onDeleteRow: VoidFunction;
};

export default function AdvancedPaymentTableRow({ row, selected, onEditRow, onDeleteRow }: Props) {
  const { _id, employee, amountInCents, paidDate, updatedAt } = row;

  const employeeName = `${employee.firstName}${employee.lastName ? ` ${employee.lastName}` : ''}`;

  const { user: authUser } = useAuthContext();

  const confirm = useBoolean();

  const popover = usePopover();

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell align="left">
          <ListItemText
            primary={employeeName}
            secondary={employee.employeeNumber}
            primaryTypographyProps={{ typography: 'body2' }}
            secondaryTypographyProps={{
              component: 'span',
              color: 'text.disabled',
            }}
          />
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
          {fCurrencyRupees(amountInCents)}
        </TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDateTime(paidDate)}</TableCell>

        <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
          {fDateTime(updatedAt)}
        </TableCell>

        <TableCell align="right" sx={{ px: 1, whiteSpace: 'nowrap' }}>
          {hasPermission(
            authUser,
            'any',
            AuthPermissions.UPDATE_ADVANCED_PAYMENT,
            AuthPermissions.DELETE_ADVANCED_PAYMENT,
            AuthPermissions.ADMIN_PERMISSION,
            AuthPermissions.SUPER_ADMIN_PERMISSION
          ) && (
            <Tooltip title="More options">
              <IconButton
                color={popover.open ? 'inherit' : 'default'}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  popover.onOpen(e);
                }}
              >
                <Iconify icon="eva:more-vertical-fill" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        arrow="right-top"
        sx={{ width: 180 }}
      >
        {hasPermission(
          authUser,
          'any',
          AuthPermissions.UPDATE_ADVANCED_PAYMENT,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) && (
          <MenuItem
            onClick={() => {
              onEditRow();
              popover.onClose();
            }}
          >
            <Iconify icon="solar:pen-bold" />
            Edit
          </MenuItem>
        )}

        {hasPermission(
          authUser,
          'any',
          AuthPermissions.DELETE_ADVANCED_PAYMENT,
          AuthPermissions.ADMIN_PERMISSION,
          AuthPermissions.SUPER_ADMIN_PERMISSION
        ) &&
          authUser?._id !== _id && (
            <MenuItem
              onClick={() => {
                confirm.onTrue();
                popover.onClose();
              }}
              sx={{ color: 'error.main' }}
            >
              <Iconify icon="solar:trash-bin-trash-bold" />
              Delete
            </MenuItem>
          )}
      </CustomPopover>

      <ConfirmDialog
        open={confirm.value}
        onClose={confirm.onFalse}
        title="Delete Advanced Payment"
        content={`Are you sure want to delete ${employeeName}'s advanced payment? This action will permenantly delete ${employeeName}'s advanced payment details from the system`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              confirm.onFalse();
              onDeleteRow();
            }}
          >
            Delete
          </Button>
        }
      />
    </>
  );
}
